import { getScaleTypeConfig } from '../../scaleTypes';
import type { ChartDataPoint } from '../../types/ChartDataPoint';
import type { XScaleType } from '../../types/Scale';
import type { XDataKey } from '../../types/XDataKey';

/**
 * Calculates or normalizes a domain for the given scale type.
 *
 * This is the main entry point for domain normalization. It delegates to the appropriate
 * normalizer strategy based on the scale type (registered in SCALE_TYPE_REGISTRY).
 *
 * ## Behavior
 *
 * - **With zoomDomain**: Normalizes the domain to the format expected by the scale type
 * - **Without zoomDomain**: Calculates the domain from chartData (with padding for continuous scales)
 *
 * ## Cross-Chart Sync
 *
 * When charts are synced via SyncedChartProvider, domains may need to be converted between formats:
 * - Continuous scales (time, linear) use `[number, number]`
 * - Band scale uses `string[]`
 *
 * The normalizer handles this conversion automatically based on the target scale type.
 *
 * @param xScaleType - The scale type ('time', 'linear', 'band')
 * @param zoomDomain - The zoom domain to normalize, or null if not zoomed
 * @param chartData - Array of data points from the chart
 * @param xDataKey - The key in data objects that represents the x-axis value
 * @returns The domain in the appropriate format for the scale type, or null if no valid domain
 *
 * @example
 * // Calculate domain from data for a time scale
 * normalizeDomain('time', null, chartData, 'timestamp');
 * // Returns: [minTimestamp - padding, maxTimestamp + padding]
 *
 * @example
 * // Normalize a zoom domain for a band scale
 * normalizeDomain('band', [1704067200000, 1704240000000], chartData, 'timestamp');
 * // Returns: ['1704067200000', '1704153600000', '1704240000000'] (data points in range)
 */
export function normalizeDomain(
  xScaleType: XScaleType,
  zoomDomain: [number, number] | string[] | null,
  chartData: ChartDataPoint[],
  xDataKey: XDataKey,
): [number, number] | string[] | null {
  const config = getScaleTypeConfig(xScaleType);
  const normalizer = config.createDomainNormalizer();

  return normalizer.normalize({
    zoomDomain,
    chartData,
    xDataKey,
  });
}
